import {Hotkey} from '@wandb/components';
import {clamp} from 'lodash';
import React from 'react';

import {hotkeyDisplayString} from '../../../util/hotkeys';
import {useMediaBrowserModalContext} from './MediaBrowserModalContext';
import {MediaSlider} from './MediaSlider';

type MediaBrowserModalFooterProps = {
  // No props needed - everything comes from context
};

export const MediaBrowserModalFooter = React.memo(() => {
  const {updateStep, updateIndex, sliderData, sliderConfig} =
    useMediaBrowserModalContext();

  const updateStepWithConstraints = (value: number) => {
    updateStep(clamp(value, stepSlider?.min ?? 0, stepSlider?.max ?? 0));
  };

  const updateIndexWithConstraints = (value: number) => {
    updateIndex(clamp(value, indexSlider?.min ?? 0, indexSlider?.max ?? 0));
  };

  const {stepSlider, indexSlider} = sliderData;

  // Configuration objects to reduce duplication
  const sliderConfigs = {
    step: {
      label: stepSlider?.label ?? 'Step',
      tooltipContent: (
        <>
          <Hotkey
            keys={hotkeyDisplayString('nextStep')}
            name="Next step"
            m={2}
          />
          or
          <Hotkey
            keys={hotkeyDisplayString('prevStep')}
            name="Previous step"
            m={2}
          />
        </>
      ),
      dataTestId: 'step-slider-root',
      strideLength: sliderConfig?.stepStrideLength,
      snapToExistingStep: sliderConfig?.snapToExistingStep,
    },
    index: {
      label: 'Index',
      tooltipContent: (
        <>
          <Hotkey keys={['Alt', '→']} name="Next index" m={2} />
          or
          <Hotkey keys={['Alt', '←']} name="Previous index" m={2} />
        </>
      ),
      dataTestId: 'index-slider-root',
      strideLength: 1,
    },
  };

  if (!stepSlider && !indexSlider) {
    return null;
  }

  const stepSliderActive =
    stepSlider && stepSlider.max > 0 && stepSlider.min < stepSlider.max;
  const indexSliderActive = indexSlider && indexSlider.max > 0;

  return (
    <div className="night-aware w-full bg-white px-16 py-16 border-t border-moon-250">
      <div className="flex w-full items-center justify-center">
        <div className="flex w-full items-center justify-center gap-40">
          {stepSliderActive && stepSlider && (
            <MediaSlider
              label={sliderConfigs.step.label}
              min={stepSlider.min}
              max={stepSlider.max}
              current={stepSlider.current}
              onChange={updateStepWithConstraints}
              tooltipContent={sliderConfigs.step.tooltipContent}
              dataTestId={sliderConfigs.step.dataTestId}
              strideLength={sliderConfigs.step.strideLength}
              ticks={stepSlider.steps}
              showTicks={false}
              snapToExistingStep={sliderConfigs?.step?.snapToExistingStep}
            />
          )}

          {indexSliderActive && indexSlider && (
            <MediaSlider
              label={sliderConfigs.index.label}
              min={indexSlider.min}
              max={indexSlider.max}
              current={indexSlider.current}
              onChange={updateIndexWithConstraints}
              tooltipContent={sliderConfigs.index.tooltipContent}
              dataTestId={sliderConfigs.index.dataTestId}
              strideLength={sliderConfigs.index.strideLength}
              ticks={indexSlider.indices}
              showTicks={false}
            />
          )}
        </div>
      </div>
    </div>
  );
});
